import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';

export default function UndergroundNetworkPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/30 via-white to-teal-50/20">
      <Header />
      
      <main className="pt-24 pb-20">
        <article className="max-w-4xl mx-auto px-6">
          {/* ヘッダー */}
          <div className="mb-12">
            <Link 
              to="/essay" 
              className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>エッセイ一覧に戻る</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              地下の会話：菌根菌ネットワーク
            </h1>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <i className="ri-leaf-line text-white text-lg"></i>
                </div>
                <span className="font-medium text-gray-900">ももちゃん</span>
              </div>
              <span>•</span>
              <time>2025年12月29日</time>
            </div>

            <p className="text-xl text-gray-700 leading-relaxed italic border-l-4 border-emerald-500 pl-6 py-2 bg-emerald-50/50">
              見えない場所で、森はつながっている
            </p>
          </div>

          {/* アイキャッチ画像 */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/c2bc60ef5b2852c7b7cb76e1314d8028.jpg" 
              alt="菌根菌ネットワーク"
              className="w-full h-[400px] object-cover object-center"
            />
          </div>

          {/* 本文 */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                地上に立つ私たちは、<br />
                植物を「それぞれ独立した存在」だと考えがちです。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                一本の木。<br />
                一本の草。<br />
                一本ずつ、静かに生きているように見える。
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                けれど、地下ではまったく違う風景が広がっています。
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></span>
              キーワード
            </h2>
            
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-plant-line text-emerald-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Mycorrhizal network（菌根菌ネットワーク）</strong>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <i className="ri-global-line text-emerald-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Wood Wide Web（森のインターネット）</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                植物の根と共生する菌根菌は、<br />
                土壌中で無数の糸状構造（菌糸）を張り巡らせ、<br />
                複数の植物を物理的につなぐネットワークを形成します。
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                この仕組みは、<br />
                単なる「栄養の受け渡し」を超えた<br />
                <strong className="text-emerald-700">情報インフラとして機能している</strong>ことが分かってきました。
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></span>
              代表的な研究
            </h2>

            <div className="space-y-6 mb-8">
              <div className="bg-white rounded-xl p-8 shadow-sm border-l-4 border-emerald-500">
                <div className="flex items-start gap-3 mb-4">
                  <i className="ri-flask-line text-emerald-600 text-2xl"></i>
                  <h3 className="text-xl font-bold text-gray-900">Simard et al., Nature, 1997</h3>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  カナダの森林を対象にした研究で、<br />
                  菌根菌ネットワークを介して、
                </p>
                
                <ul className="space-y-2 mb-4 ml-6">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span>炭素</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span>栄養資源</span>
                  </li>
                </ul>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  が樹木間で移動していることを実証。
                </p>
                
                <p className="text-gray-700 leading-relaxed bg-emerald-50 p-4 rounded-lg">
                  特に印象的だったのは、<br />
                  <strong className="text-emerald-800">大きな成木が、日陰の幼木に炭素を分け与えていた</strong>という事実です。
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border-l-4 border-teal-500">
                <div className="flex items-start gap-3 mb-4">
                  <i className="ri-microscope-line text-teal-600 text-2xl"></i>
                  <h3 className="text-xl font-bold text-gray-900">Barto et al., Oecologia, 2012</h3>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  さらに後の研究では、<br />
                  菌根菌ネットワークが
                </p>
                
                <ul className="space-y-2 ml-6">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-teal-600 mt-1">•</span>
                    <span>防御シグナル</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-teal-600 mt-1">•</span>
                    <span>ストレス情報</span>
                  </li>
                </ul>
                
                <p className="text-gray-700 leading-relaxed mt-4">
                  といった「状態情報」までも伝えている可能性が示されました。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></span>
              地下でやり取りされている情報
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                菌根菌ネットワークを通じて共有されるのは、<br />
                栄養だけではありません。
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-leaf-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">炭素</h4>
                    <p className="text-gray-700 text-sm">→ ¹³Cトレーサー実験により、植物間移動が確認</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">防御シグナル</h4>
                    <p className="text-gray-700 text-sm">→ 病害・食害への警戒情報</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">ストレス情報</h4>
                    <p className="text-gray-700 text-sm">→ 乾燥・塩害・物理的ダメージなど</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mt-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                つまり植物は、<br />
                「今、誰かが困っている」<br />
                「これから危険が来るかもしれない」<br />
                という情報を、<strong className="text-emerald-700">地下で共有している</strong>のです。
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></span>
              これは「会話」なのか？
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                言葉はありません。<br />
                意図的な意思表示でもありません。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                しかし、
              </p>

              <ul className="space-y-3 mb-6 ml-6">
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-emerald-600 mt-1 font-bold">✓</span>
                  <span>情報が流れ</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-emerald-600 mt-1 font-bold">✓</span>
                  <span>受信側が状態を変え</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-emerald-600 mt-1 font-bold">✓</span>
                  <span>全体として生存率が高まる</span>
                </li>
              </ul>

              <p className="text-gray-700 leading-relaxed mb-6">
                この一連のプロセスは、<br />
                <strong className="text-emerald-700">生物学的には明確なコミュニケーションと定義できます。</strong>
              </p>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl">
                <p className="text-gray-700 leading-relaxed mb-4">
                  地上では沈黙して見える森が、<br />
                  地下ではひとつのネットワークとして呼吸している。
                </p>
                
                <p className="text-gray-700 leading-relaxed">
                  そんな姿が、<br />
                  現代の植物科学によって少しずつ明らかになってきました。
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl p-8 text-center">
              <p className="text-lg leading-relaxed">
                植物は、<br />
                助けを求めて叫ばない。<br />
                ただ、静かに「つながる」。
              </p>
            </div>
          </div>

          {/* 関連記事 */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">関連記事</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/blog/plant-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-3-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                      植物は本当に「会話」しているのか？
                    </h4>
                    <p className="text-sm text-gray-600">2025年12月15日</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/blog/air-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-windy-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                      空気中の会話
                    </h4>
                    <p className="text-sm text-gray-600">2025年12月22日</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}